import '@/assets/styles.scss';
import '@/assets/tailwind.css';
import { initializeAuthListener } from '@/service/authListner.js';
import Aura from '@primevue/themes/aura';
import * as Sentry from '@sentry/vue';
import { MotionPlugin } from '@vueuse/motion';
import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { createApp } from 'vue';
import App from './App.vue';
import i18n from './i18n';
import router from './router';
import { supabase } from './supabase'; // ✅ make sure this import exists

initializeAuthListener();

(async () => {
    try {
        const {
            data: { user },
            error
        } = await supabase.auth.getUser();

        if (error || !user) {
            localStorage.clear();
            sessionStorage.clear();

            await supabase.auth.signOut();
        }
    } catch (err) {
        localStorage.clear();
        sessionStorage.clear();
    }
})();

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App);
app.config.globalProperties.$supabase = supabase;

Sentry.init({
    app,
    dsn: 'https://ab8fecc5e5755fd63b549bb23d388942@o4508595331792896.ingest.de.sentry.io/4510469100601424',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/dev\.digiquiz\.io\/api/],

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Logs
    enableLogs: true
});

import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/userStore';

app.use(router);
app.use(i18n);
app.use(pinia);
app.use(MotionPlugin);
app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: { darkModeSelector: '.app-dark' }
    }
});
app.use(ToastService);
app.use(ConfirmationService);
app.mount('#app');
// Init auth store and restore user session
const authStore = useAuthStore(pinia);
authStore.init();

// Restore userStore from Supabase session if empty (persist: false case)
const initUserStore = async () => {
    const userStore = useUserStore(pinia);

    if (!userStore.id) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user?.id) {
            const authUid = sessionData.session.user.id;
            const { data: userData, error: userError } = await supabase
                .from('users')
                .select('*')
                .eq('auth_uid', authUid)
                .maybeSingle();

            if (userData && !userError) {
                userStore.setUser({
                    id: userData.id,
                    authUid: userData.auth_uid,
                    role: userData.role,
                    mobile: userData.mobile,
                    name: userData.name,
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    email: userData.email || '',
                    profileImg: userData.profile_img,
                    country: userData.country || '',
                    state: userData.state || '',
                    city: userData.city || ''
                });
            }
        }
    }
};

initUserStore();


// ✅ Optional: request permission for notifications when app starts
// requestNotificationPermissionWithSW();
